import {checkTimeAvailability} from '@functions/helpers/datetime/prepareTime';
import {DISPLAY_POP_UP_DURING_BUSINESS, DISPLAY_POP_UP_ALL_TIME} from '@assets/const/welcomePopUp';
import {isEmpty} from '@avada/utils';
import {CAMPAIGN_TEMPLATE_ID} from '@assets/const/campaignTemplate';
import {DEFAULT_CAMPAIGN_PAGE_DISPLAY_ON} from '@assets/helpers/campaign/defaultCampaignSetting';

const WELCOME_POPUP_COUNT_LOCAL_KEY = 'avada-chatty/welcome-popup';
const NEWSLETTER_POPUP_COUNT_LOCAL_KEY = 'avada-chatty/newsletter-popup';
const CAMPAIGN_POPUP_COUNT_LOCAL_KEY = 'avada-chatty/campaign-popup';
const CAMPAIGN_POPUP_GLOBAL_SHOWED_LOCAL_KEY = 'avada-chatty/campaign-popup-global';
const CAMPAIGN_MESSAGES_LOCAL_KEY = 'avada-chatty/campaign-messages';

const getLocalStorage = key => localStorage.getItem(key);

const setLocalStorage = (key, val) => localStorage.setItem(key, val);

const removeLocalStorage = key => localStorage.removeItem(key);

export const getWelcomePopupShowed = () => {
  return parseInt(getLocalStorage(WELCOME_POPUP_COUNT_LOCAL_KEY) || '0');
};

export const getNewsletterPopupShowed = () => {
  return parseInt(getLocalStorage(NEWSLETTER_POPUP_COUNT_LOCAL_KEY) || '0');
};

export const addWelcomePopupShowed = () => {
  return setLocalStorage(WELCOME_POPUP_COUNT_LOCAL_KEY, getWelcomePopupShowed() + 1);
};

export const addNewsletterPopupShowed = () => {
  return setLocalStorage(NEWSLETTER_POPUP_COUNT_LOCAL_KEY, getNewsletterPopupShowed() + 1);
};

export const checkShowWelcomePopup = () => {
  const {widgetSetting: settings = {}} = window.AVADA_FAQ;
  const {welcomePopupSetting, isEnableWelcomePopup, isPublishCampaign} = settings;
  const {audience = ['all'], device = ['all'], statusTeaser} = welcomePopupSetting || {};

  return (
    !isPublishCampaign && // Don't show welcome popup if already published campaign
    isEnableWelcomePopup &&
    statusTeaser &&
    getWelcomePopupShowed() < 1 &&
    checkDisplayTime(settings) &&
    checkDisplayDevice(device) &&
    checkDisplayAudience(audience)
  );
};

const checkDisplayTime = (settings, val) => {
  const displayTime = val || settings?.welcomePopupSetting?.displayTimeShowWelcome;

  return (
    displayTime === DISPLAY_POP_UP_ALL_TIME ||
    (displayTime === DISPLAY_POP_UP_DURING_BUSINESS && checkTimeAvailability(settings))
  );
};

const checkDisplayDevice = device => {
  if (isEmpty(device)) return false;
  const width = window.innerWidth || 0;
  switch (device[0]) {
    case 'only_mobile':
      return width <= 750;
    case 'only_desktop':
      return width > 750;
    default:
      return true;
  }
};

const checkMinimumCartValue = campaign => {
  if (campaign?.templateId !== CAMPAIGN_TEMPLATE_ID.CART_BOOSTER) return true;
  const {minimumCartValue} = campaign?.trigger || {};
  const cartTracking = localStorage.getItem('avada-chatty/cart-tracking');
  if (!cartTracking) return false;
  const cartTrackingData = cartTracking ? JSON.parse(cartTracking) : null;
  const cartTotalPrice = cartTrackingData ? Number(cartTrackingData.totalPrice / 100) : 0;
  const currencyExchangeRate = window.Shopify?.currency?.rate || 1;
  if (
    cartTrackingData &&
    cartTrackingData.lastUpdate &&
    cartTrackingData.quantity > 0 &&
    cartTotalPrice >= minimumCartValue * currencyExchangeRate
  ) {
    return true;
  }
  return false;
};

const checkDisplayAudience = audience => {
  if (isEmpty(audience)) return false;
  const {id} = window.AVADA_APP_FAQS_CUSTOMER || {};
  switch (audience[0]) {
    case 'only_visitors':
      return !id;
    case 'only_customers':
      return !!id;
    default:
      return true;
  }
};

/**
 *
 * @param {boolean} checkShowForm
 * @returns {boolean}
 */
export const checkShowNewsletterPopup = (checkShowForm = false) => {
  const {widgetSetting: settings = {}} = window.AVADA_FAQ;
  const {accepts_marketing} = window.AVADA_APP_FAQS_CUSTOMER;

  const {isEnableNewsletterPopup, isPublishCampaign} = settings;

  // Don't show newsletter popup if already published campaign
  if (!isEnableNewsletterPopup || accepts_marketing || isPublishCampaign) return false;
  // ignore below condition if the subscribe form should be shown
  return checkShowForm || getNewsletterPopupShowed() < 1;
};

export const checkActiveCampaign = (campaigns = [], isCheckPopupShowed = true) => {
  const {widgetSetting: settings} = window.AVADA_FAQ || {};
  const activeCampaigns = campaigns.filter(
    c =>
      (isCheckPopupShowed ? getCampaignPopupShowed(c.id) < 1 : true) &&
      // getCampaignPopupGlobalShowed() < 1 && Temporary disable global popup
      checkPageDisplayOn(c) &&
      checkDisplayAudience([c.condition?.audience]) &&
      checkDisplayTime(settings, c.condition?.displayTime) &&
      checkDisplayDevice([c.condition?.device]) &&
      checkMinimumCartValue(c)
  );

  return activeCampaigns;
};

export const checkActiveCampaignType = (campaigns = []) => {
  const activeCampaigns = checkActiveCampaign(campaigns, false) || [];
  const activeCampaignTypeOrder = new Map();

  activeCampaigns.forEach(c => {
    if (!activeCampaignTypeOrder.has(c.templateId)) {
      activeCampaignTypeOrder.set(c.templateId, c);
    }
  });

  return activeCampaignTypeOrder;
};

const checkPageDisplayOn = campaign => {
  if (!campaign) return false;
  const templateId = campaign.templateId;

  if (templateId === CAMPAIGN_TEMPLATE_ID.WELCOME_VISITORS) return window.location.pathname === '/';
  const {type, value} = campaign.trigger.displayOn;
  const productGid = window.ShopifyAnalytics?.meta?.product?.gid;

  switch (type) {
    case DEFAULT_CAMPAIGN_PAGE_DISPLAY_ON.ALL:
      return true;
    case DEFAULT_CAMPAIGN_PAGE_DISPLAY_ON.SPECIFIC:
      return value?.includes(window.location.href);
    case DEFAULT_CAMPAIGN_PAGE_DISPLAY_ON.PRODUCT:
      return !!productGid;
    case DEFAULT_CAMPAIGN_PAGE_DISPLAY_ON.SPECIFIC_PRODUCT:
      return (
        value?.includes(productGid) || value?.map(el => el.id)?.includes(productGid) // For new campaign trigger that contains product object instead of only id
      );
    default:
      return false;
  }
};

export const addCampaignPopupShowed = campaignIds => {
  const count = JSON.parse(getLocalStorage(CAMPAIGN_POPUP_COUNT_LOCAL_KEY) || '{}');
  campaignIds.forEach(campaignId => {
    count[campaignId] = (count[campaignId] || 0) + 1;
  });
  setLocalStorage(CAMPAIGN_POPUP_COUNT_LOCAL_KEY, JSON.stringify(count));
};

export const getCampaignPopupShowed = campaignId => {
  const count = JSON.parse(getLocalStorage(CAMPAIGN_POPUP_COUNT_LOCAL_KEY) || '{}');
  return parseInt(count[campaignId] || '0');
};

export const addCampaignPopupGlobalShowed = () => {
  return setLocalStorage(
    CAMPAIGN_POPUP_GLOBAL_SHOWED_LOCAL_KEY,
    getCampaignPopupGlobalShowed() + 1
  );
};

export const getCampaignPopupGlobalShowed = () => {
  return parseInt(getLocalStorage(CAMPAIGN_POPUP_GLOBAL_SHOWED_LOCAL_KEY) || '0');
};

export const addCampaignMessages = messages => {
  return setLocalStorage(CAMPAIGN_MESSAGES_LOCAL_KEY, JSON.stringify(messages));
};

export const getCampaignMessages = () => {
  return getLocalStorage(CAMPAIGN_MESSAGES_LOCAL_KEY);
};

export const removeCampaignMessages = () => {
  return removeLocalStorage(CAMPAIGN_MESSAGES_LOCAL_KEY);
};
